import { useState } from "react";
import { Ban, Linkedin, Mail, Pencil, Phone, Plus, Star, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { SectionCard } from "@/features/power_projects/components/ui";
import {
  useAddContact,
  useDeleteContact,
  useUpdateContact,
  type Lead,
  type LeadContact,
} from "@/features/leads/api/leads";

/**
 * Osoby kontaktowe.
 *
 * Wiele na firmę celowo: piszemy do człowieka, nie do `biuro@`, a osoba oznaczona
 * gwiazdką jest domyślnym adresatem. Adres, który sam poprosił o wypisanie, jest
 * oznaczony osobno — blokada bywa na osobie, nie na całej firmie.
 */

const EMPTY = {
  first_name: "",
  honorific: "",
  salutation_override: "",
  last_name: "",
  position: "",
  email: "",
  phone: "",
  linkedin_url: "",
};

/**
 * Podpowiedź formy grzecznościowej po imieniu.
 *
 * Reguła „imię na -a znaczy kobieta" jest w polszczyźnie mocna, ale nie pewna
 * (Kuba, Barnaba, imiona obce), dlatego to TYLKO podpowiedź w formularzu —
 * człowiek ją widzi przed zapisem i może zmienić. System nigdy nie zgaduje płci
 * w momencie wysyłki: pusta wartość to „Szanowni Państwo", co zawsze jest
 * poprawne, a pomyłka w pierwszym zdaniu zimnego maila kosztuje ten mail.
 */
const MALE_ENDING_IN_A = ["kuba", "barnaba", "bonawentura", "juda"];

// Podgląd zwrotu liczy backend (wołacz imienia). Tu podpowiadamy tylko Pan/Pani.

function suggestHonorific(firstName: string): string {
  const name = firstName.trim().toLowerCase();
  if (name.length < 3) return "";
  if (MALE_ENDING_IN_A.includes(name)) return "pan";
  return name.endsWith("a") ? "pani" : "pan";
}

/**
 * Poprawka danych już zapisanej osoby.
 *
 * Telefon i mail zmieniają się częściej niż cokolwiek innego na tej karcie
 * (ktoś odchodzi, firma zmienia domenę), a jedyną drogą było dotąd „usuń i dodaj
 * od nowa" — co kasowało przy okazji historię kontaktu z tą osobą.
 *
 * UWAGA: adres w TRWAJĄCEJ kampanii jest zamrożony w chwili dodania firmy, więc
 * zmiana tutaj dotyczy przyszłych wysyłek, a nie maili czekających już w kolejce.
 */
function ContactEditor({
  person,
  onDone,
}: {
  person: LeadContact;
  onDone: () => void;
}) {
  const updateContact = useUpdateContact();
  const [draft, setDraft] = useState({
    first_name: person.first_name,
    last_name: person.last_name,
    position: person.position,
    email: person.email,
    phone: person.phone,
    linkedin_url: person.linkedin_url,
  });
  const [error, setError] = useState("");

  const set = (field: keyof typeof draft) => (e: { target: { value: string } }) =>
    setDraft({ ...draft, [field]: e.target.value });

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <input
          className="input-base"
          placeholder="Imię"
          value={draft.first_name}
          onChange={set("first_name")}
        />
        <input
          className="input-base"
          placeholder="Nazwisko"
          value={draft.last_name}
          onChange={set("last_name")}
        />
      </div>
      <input
        className="input-base"
        placeholder="Stanowisko"
        value={draft.position}
        onChange={set("position")}
      />
      <input
        className="input-base"
        placeholder="E-mail"
        value={draft.email}
        onChange={set("email")}
      />
      <input
        className="input-base"
        placeholder="Telefon"
        value={draft.phone}
        onChange={set("phone")}
      />
      <input
        className="input-base"
        placeholder="LinkedIn"
        value={draft.linkedin_url}
        onChange={set("linkedin_url")}
      />
      {error && <p className="text-xs text-[var(--color-primary)]">{error}</p>}
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          loading={updateContact.isPending}
          onClick={() => {
            if (!draft.first_name && !draft.last_name && !draft.email && !draft.phone) {
              setError("Podaj przynajmniej imię, nazwisko, e-mail albo telefon.");
              return;
            }
            setError("");
            updateContact.mutate(
              { id: person.id, ...draft },
              {
                onSuccess: onDone,
                onError: () => setError("Nie udało się zapisać zmian."),
              },
            );
          }}
        >
          Zapisz
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>
          Anuluj
        </Button>
      </div>
    </div>
  );
}

export function LeadContactsPanel({ lead }: { lead: Lead }) {
  const addContact = useAddContact();
  const updateContact = useUpdateContact();
  const deleteContact = useDeleteContact();

  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ ...EMPTY });
  const [error, setError] = useState("");

  const submit = () => {
    if (!draft.first_name && !draft.last_name && !draft.email && !draft.phone) {
      setError("Podaj przynajmniej imię, nazwisko, e-mail albo telefon.");
      return;
    }
    setError("");
    addContact.mutate(
      { leadId: lead.id, ...draft, is_primary: lead.contacts.length === 0 },
      {
        onSuccess: () => {
          setDraft({ ...EMPTY });
          setAdding(false);
        },
        onError: () => setError("Nie udało się dodać osoby."),
      },
    );
  };

  return (
    <SectionCard
      title="Osoby kontaktowe"
      action={
        <button
          type="button"
          onClick={() => setAdding((value) => !value)}
          className="font-mono text-[11px] uppercase tracking-wider text-[var(--color-primary)]"
        >
          <Plus size={12} className="mr-1 inline" />
          Dodaj
        </button>
      }
    >
      {adding && (
        <div className="mb-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <input
              className="input-base"
              placeholder="Imię"
              value={draft.first_name}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  first_name: e.target.value,
                  // Podpowiedź tylko dopóki nikt nie wybrał sam — raz ustawiona
                  // ręcznie forma nie ma się zmieniać przy poprawianiu literówki.
                  honorific: draft.honorific || suggestHonorific(e.target.value),
                })
              }
            />
            <input
              className="input-base"
              placeholder="Nazwisko"
              value={draft.last_name}
              onChange={(e) =>
                setDraft({ ...draft, last_name: e.target.value })
              }
            />
          </div>
          <div>
            <select
              className="input-base"
              value={draft.honorific}
              onChange={(e) => setDraft({ ...draft, honorific: e.target.value })}
            >
              <option value="">Bez formy — „Szanowni Państwo”</option>
              <option value="pan">Pan — „Szanowny Panie {"{nazwisko}"}”</option>
              <option value="pani">Pani — „Szanowna Pani {"{nazwisko}"}”</option>
            </select>
            <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">
              Tak zacznie się mail do tej osoby. Podpowiedź po imieniu bywa myląca
              (Kuba, imiona obce) — proszę sprawdzić przed zapisem.
            </p>
          </div>
          <input
            className="input-base"
            placeholder="Stanowisko"
            value={draft.position}
            onChange={(e) => setDraft({ ...draft, position: e.target.value })}
          />
          <input
            className="input-base"
            placeholder="E-mail"
            value={draft.email}
            onChange={(e) => setDraft({ ...draft, email: e.target.value })}
          />
          <input
            className="input-base"
            placeholder="Telefon"
            value={draft.phone}
            onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
          />
          <input
            className="input-base"
            placeholder="LinkedIn"
            value={draft.linkedin_url}
            onChange={(e) =>
              setDraft({ ...draft, linkedin_url: e.target.value })
            }
          />
          <Button size="sm" onClick={submit} loading={addContact.isPending}>
            Zapisz osobę
          </Button>
          {error && (
            <p className="text-xs text-[var(--color-primary)]">{error}</p>
          )}
        </div>
      )}

      {lead.contacts.length === 0 ? (
        <p className="text-sm text-[var(--color-text-muted)]">Brak osób.</p>
      ) : (
        <ul className="space-y-3 text-sm">
          {lead.contacts.map((person: LeadContact) => (
            <li
              key={person.id}
              className="border-b border-[var(--color-border)] pb-2 last:border-0"
            >
              {editing === person.id ? (
                <ContactEditor person={person} onDone={() => setEditing(null)} />
              ) : (
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium text-[var(--color-text-primary)]">
                      {person.display_name || person.email || "(bez nazwiska)"}
                    </span>
                    {person.is_primary && (
                      <Star
                        size={12}
                        className="text-[var(--color-primary)]"
                        aria-label="Kontakt główny"
                      />
                    )}
                    {person.is_blocked && (
                      <span
                        className="flex items-center gap-1 text-xs text-[var(--color-primary)]"
                        title="Ten adres jest na liście „nie kontaktować”"
                      >
                        <Ban size={11} /> nie pisać
                      </span>
                    )}
                  </div>
                  {person.position && (
                    <div className="text-xs text-[var(--color-text-muted)]">
                      {person.position}
                    </div>
                  )}
                  {/* Do zmiany także po dodaniu osoby — kontakty pobrane z KRS
                      przychodzą bez formy, a to od niej zaczyna się mail. */}
                  <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px]">
                    <span className="text-[var(--color-text-muted)]">
                      Mail zacznie się od:
                    </span>
                    <span className="text-[var(--color-text-primary)]">
                      „{person.salutation},”
                    </span>
                    <select
                      className="border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-1.5 py-0.5 text-[11px]"
                      value={person.honorific}
                      onChange={(e) =>
                        updateContact.mutate({
                          id: person.id,
                          honorific: e.target.value,
                        })
                      }
                      title="Forma grzecznościowa użyta w mailu do tej osoby"
                    >
                      <option value="">Państwo</option>
                      <option value="pan">Pan</option>
                      <option value="pani">Pani</option>
                    </select>
                    <input
                      className="w-40 border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-1.5 py-0.5 text-[11px]"
                      defaultValue={person.salutation_override}
                      placeholder="popraw ręcznie…"
                      title="Dla imion, których odmiana wychodzi źle — np. obcych"
                      onBlur={(e) => {
                        if (e.target.value === person.salutation_override) return;
                        updateContact.mutate({
                          id: person.id,
                          salutation_override: e.target.value,
                        });
                      }}
                    />
                  </div>
                  <div className="mt-1 space-y-0.5 text-xs text-[var(--color-text-secondary)]">
                    {person.email && (
                      <div className="flex items-center gap-1">
                        <Mail size={11} />
                        <a
                          href={`mailto:${person.email}`}
                          className="hover:underline"
                        >
                          {person.email}
                        </a>
                      </div>
                    )}
                    {person.phone && (
                      <div className="flex items-center gap-1">
                        <Phone size={11} />
                        <a
                          href={`tel:${person.phone}`}
                          className="hover:underline"
                        >
                          {person.phone}
                        </a>
                      </div>
                    )}
                    {person.linkedin_url && (
                      <div className="flex items-center gap-1">
                        <Linkedin size={11} />
                        <a
                          href={person.linkedin_url}
                          target="_blank"
                          rel="noreferrer"
                          className="hover:underline"
                        >
                          profil
                        </a>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 gap-2">
                  <button
                    type="button"
                    title="Popraw dane osoby (telefon, e-mail, stanowisko)"
                    onClick={() => setEditing(person.id)}
                    className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                  >
                    <Pencil size={13} />
                  </button>
                  {!person.is_primary && (
                    <button
                      type="button"
                      title="Ustaw jako kontakt główny"
                      onClick={() =>
                        updateContact.mutate({
                          id: person.id,
                          is_primary: true,
                        })
                      }
                      className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                    >
                      <Star size={13} />
                    </button>
                  )}
                  <button
                    type="button"
                    title="Usuń osobę"
                    onClick={() => deleteContact.mutate(person.id)}
                    className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}
